import {AppliedFilterSection_Value} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/filter_section.types';
import {useHistory} from '@aboutyou/router';
import {useLocalStorage} from '@aboutyou/storage';
import {useTrackFeature} from '@aboutyou/tracking';
import {useMemo} from 'react';
import {v4 as uuidv4} from 'uuid';
import {useNormalizedAppliedFilters} from '../../shared/useNormalizedAppliedFilters';

const PERSONALIZED_FILTER_STORE_KEY = 'personalized-filters';

export interface PersonalizedFilterStore {
  appliedFiltersData: AppliedFilterSection_Value[];
  categoryName: string;
}

export interface PersonalizedFilter {
  id: string;
  categoryName: string;
  fullUrl: string;
  filters: string[];
}

export const usePersonalizedFiltersStore = ({
  appliedFiltersData,
  categoryName,
}: PersonalizedFilterStore) => {
  const trackFeature = useTrackFeature();

  const normalizedFilters = useNormalizedAppliedFilters(appliedFiltersData);
  const {location} = useHistory();
  const fullUrl = `${location.pathname}${location.search}`;

  const [personalizedFilters, setPersonalizedFilter] = useLocalStorage<
    PersonalizedFilter[]
  >({
    defaultValue: [],
    key: PERSONALIZED_FILTER_STORE_KEY,
  });

  const currentFilterCombinationAlreadyExists = useMemo(
    () => personalizedFilters?.some(filter => filter.fullUrl === fullUrl),
    [fullUrl, personalizedFilters],
  );

  const appliedPersonalizedFilterId = useMemo(
    () => personalizedFilters?.find(filter => filter.fullUrl === fullUrl)?.id,
    [fullUrl, personalizedFilters],
  );

  const savePersonalizedFilter = () => {
    setPersonalizedFilter([
      {
        id: uuidv4(),
        categoryName: categoryName.toLowerCase(),
        fullUrl,
        filters: normalizedFilters.map(filter => filter.label),
      },
      ...(personalizedFilters || []),
    ]);

    trackFeature({
      eventType: 'click',
      label: `persoFilter|saveFilter|${categoryName.toLowerCase()}|${
        normalizedFilters.length
      }`,
    });
  };

  const deletePersonalizedFilter = (personalizedFilter: PersonalizedFilter) => {
    setPersonalizedFilter([
      ...(personalizedFilters || []).filter(
        filter => filter.id !== personalizedFilter.id,
      ),
    ]);

    trackFeature({
      eventType: 'click',
      label: `persoFilter|deleteFilter|${personalizedFilter.categoryName}|${personalizedFilter.filters.length}`,
    });
  };

  return {
    personalizedFilters,
    savePersonalizedFilter,
    deletePersonalizedFilter,
    currentFilterCombinationAlreadyExists,
    appliedPersonalizedFilterId,
  };
};
